import { useQueryClient } from '@tanstack/react-query';
import { Row } from '@tanstack/react-table';
import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/Button/Button';
import { ButtonVariants } from '../../../components/Button/Button.types';
import Header from '../../../components/Header/Header';
import TableCard from '../../../components/Tables/TableCard/TableCard';
import { showToast } from '../../../components/Toast/Toast';
import { ToastTypes } from '../../../components/Toast/Toast.types';
import { useFranchiseePortalUsers } from '../../../services/user/user.service';
import {
  FRANCHISEE_IMPERSONATION,
  LOGIN_ID_HEADER,
  ORG_ID_HEADER,
  User,
  UserRoles,
  UserTypes,
} from '../../../types/auth.types';
import useFranchiseeAlias from '../../../utils/useFranchiseeAlias/useFranchiseeAlias';
import useLocalUser from '../../../utils/useLocalUser/useLocalUser';
import styles from './FranchiseePortal.module.css';
import { useTranslation } from 'react-i18next';
import { useSTKContext } from '@/context/context';

const ViewButton = ({ row }: { row: Row<User> }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setAlias, setActualUser } = useFranchiseeAlias();
  const { setUser } = useLocalUser();
  const queryClient = useQueryClient();
  const { apiClient } = useSTKContext();

  return (
    <Button
      variant={ButtonVariants.Text}
      onClick={() => {
        apiClient.defaults.headers.common[ORG_ID_HEADER] = row.original.orgId;
        apiClient.defaults.headers.common[LOGIN_ID_HEADER] = row.original.loginId || FRANCHISEE_IMPERSONATION;
        setActualUser({ ...row.original, type: UserTypes.FRANCHISEE });
        setAlias({ ...row.original, type: UserTypes.FRANCHISEE });
        setUser({ ...row.original, type: UserTypes.FRANCHISEE });
        sessionStorage.setItem('aliasInfo', JSON.stringify(row.original));
        queryClient.invalidateQueries(['current-user']);
        navigate('/');
      }}
    >
      {t('common.select')}
    </Button>
  );
};

const FranchiseePortal: FC = () => {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useFranchiseePortalUsers(true);

  if (isError) {
    showToast({
      label: t('error.fetchingFranchiseePortalUsers'),
      toastType: ToastTypes.Error,
      id: 'errorFetchingFranchiseePortalUsers',
    });
  }
  return (
    <div>
      <Header title={t('franchiseePortal.title')} breadcrumb={{ label: t('breadcrumb.backToHome'), to: '/' }} />
      <div className={styles.page}>
        <div className={styles.description}>{t('franchiseePortal.header')}</div>
        <TableCard
          width="100%"
          className={styles.tableCard}
          enableFilters
          returnedRecordsText={(count) => t('franchiseePortal.table.returnedRecord', { count })}
          isLoading={isLoading}
          data={data?.data || []}
          columns={[
            {
              header: t('franchiseePortal.table.user'),
              enableSorting: true,
              accessorFn: (row) =>
                row.firstName || row.lastName ? `${row.firstName || ''} ${row.lastName || ''}` : '-',
            },
            { header: t('franchiseePortal.table.email'), enableSorting: true, accessorFn: (row) => row.email || '-' },
            {
              id: 'role',
              header: t('franchiseePortal.table.access'),
              enableSorting: true,
              accessorFn: (row) => (row.role === UserRoles.VIEW ? t('common.viewOnly') : t('common.edit')),
            },
            { header: t('franchiseePortal.table.organization'), enableSorting: true, accessorKey: 'fzCode' },
            { header: '', accessorKey: 'id', cell: ViewButton, enableColumnFilter: false },
          ]}
        />
      </div>
    </div>
  );
};

export default FranchiseePortal;
